/**
 * Version Display Component for MacroDesk
 * Shows documentation versions and provides update functionality
 */

import React, { useState, useEffect } from "react";

const VersionDisplay = ({ isMA3, onKeywordsUpdate }) => {
  const [versions, setVersions] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateProgress, setUpdateProgress] = useState(0);
  const [updateMessage, setUpdateMessage] = useState("");

  // Load version information
  useEffect(() => {
    loadVersions();
  }, [isMA3]);

  const loadVersions = async () => {
    try {
      const response = await fetch("/api/versions");
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setVersions(data);
    } catch (error) {
      console.error("Error loading versions:", error);
    }
  };

  // Handle manual update
  // Parsing requires Node.js (cheerio/axios), so we try the local parser server first.
  // The Cloudflare Worker stub at /api/parse-* cannot do actual parsing.
  const LOCAL_PARSER_URL = "http://localhost:3001";

  const handleUpdate = async () => {
    setIsUpdating(true);
    setUpdateProgress(0);
    setUpdateMessage("Starting update...");

    try {
      const platform = isMA3 ? "ma3" : "ma2";

      setUpdateProgress(10);
      setUpdateMessage("Connecting to local parser server...");

      let response;
      let usedLocal = false;

      // 1. Try local parser server first (supports both route formats)
      try {
        response = await fetch(`${LOCAL_PARSER_URL}/api/parse/${platform}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        });
        usedLocal = true;
      } catch (_localErr) {
        // Local server not running — fall back to relative URL (Cloudflare Worker)
        response = await fetch(`/api/parse-${platform}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        });
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setUpdateProgress(50);
      setUpdateMessage("Parsing documentation...");

      const result = await response.json();

      if (result.success) {
        setUpdateProgress(100);
        setUpdateMessage(
          `✅ Updated ${result.keywords} keywords (v${result.version})`
        );

        // Reload versions after successful update
        await loadVersions();

        // Call the callback to refresh keywords in the parent component
        if (onKeywordsUpdate) {
          onKeywordsUpdate();
        }
      } else {
        // Show a helpful message if the Worker stub responded
        if (!usedLocal && result.error?.includes("not supported")) {
          setUpdateMessage(
            "⚠️ Start the local parser server first: npm run server"
          );
        } else {
          setUpdateMessage(`❌ Update failed: ${result.error}`);
        }
      }
    } catch (error) {
      setUpdateMessage(`❌ Error: ${error.message}`);
    }

    setIsUpdating(false);
    setUpdateProgress(0);

    // Clear message after 8 seconds
    setTimeout(() => {
      setUpdateMessage("");
    }, 8000);
  };

  const getCurrentVersion = () => {
    if (!versions) return "Loading...";
    const v = isMA3 ? versions.current?.ma3 : versions.current?.ma2;
    return typeof v === 'object' ? JSON.stringify(v) : String(v || "unknown");
  };

  const getLatestVersion = () => {
    if (!versions) return "Loading...";
    const v = isMA3 ? versions.latest?.ma3?.latest : versions.latest?.ma2?.latest;
    return typeof v === 'object' ? JSON.stringify(v) : String(v || "unknown");
  };

  const compareVersions = (v1, v2) => {
    if (!v1 || !v2 || v1 === "unknown" || v2 === "unknown") return 0;
    const parts1 = v1.split('.').map(Number);
    const parts2 = v2.split('.').map(Number);
    for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
      const p1 = parts1[i] || 0;
      const p2 = parts2[i] || 0;
      if (p1 > p2) return 1;
      if (p1 < p2) return -1;
    }
    return 0;
  };

  const hasUpdate = () => {
    if (!versions) return false;
    const current = isMA3 ? versions.current?.ma3 : versions.current?.ma2;
    const latest = isMA3
      ? versions.latest?.ma3?.latest
      : versions.latest?.ma2?.latest;
    
    return compareVersions(latest, current) > 0;
  };

  return (
    <div
      className={`p-3 rounded-lg ${
        isMA3 ? "bg-gray-100 text-gray-800" : "bg-zinc-800 text-gray-200"
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <h3
          className={`text-sm font-semibold ${
            isMA3 ? "text-blue-600" : "text-yellow-400"
          }`}
        >
          Documentation Version
        </h3>
      </div>

      <div className="space-y-1 text-xs">
        <div className="flex justify-between">
          <span>Current:</span>
          <span className={isMA3 ? "text-gray-600" : "text-gray-400"}>
            {getCurrentVersion()}
          </span>
        </div>

        <div className="flex justify-between">
          <span>Latest:</span>
          <span
            className={`${
              hasUpdate()
                ? "text-green-400"
                : isMA3
                ? "text-gray-600"
                : "text-gray-400"
            }`}
          >
            {getLatestVersion()}
          </span>
        </div>

        {hasUpdate() && (
          <div className="text-green-400 text-xs mt-1">
            🎉 Update available!
          </div>
        )}
      </div>

      <div className="flex flex-col space-y-2">
        {hasUpdate() && (
          <>
            <button
              onClick={handleUpdate}
              disabled={isUpdating}
              className={`px-3 py-1 my-2 text-xs rounded transition-colors ${
                isMA3
                  ? "bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-400"
                  : "bg-yellow-400 hover:bg-yellow-500 text-black disabled:bg-gray-600"
              }`}
            >
              {isUpdating ? "Updating..." : "Update"}
            </button>

            {isUpdating && (
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-300 ${
                    isMA3 ? "bg-blue-600" : "bg-yellow-400"
                  }`}
                  style={{ width: `${updateProgress}%` }}
                ></div>
              </div>
            )}
          </>
        )}
      </div>

      {updateMessage && (
        <div
          className={`mt-2 p-2 text-xs rounded ${
            updateMessage.includes("✅")
              ? "bg-green-100 text-green-800"
              : updateMessage.includes("❌")
              ? "bg-red-100 text-red-800"
              : "bg-blue-100 text-blue-800"
          }`}
        >
          {updateMessage}
        </div>
      )}

      {versions?.timestamp && (
        <div
          className={`text-xs mt-2 ${
            isMA3 ? "text-gray-500" : "text-gray-400"
          }`}
        >
          Last checked: {new Date(versions.timestamp).toLocaleString()}
        </div>
      )}
    </div>
  );
};

export default VersionDisplay;
